import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { formatDate } from '../../../../utils/helper';
import { embedVideo } from '../../../../actionCreators/common';
import ExpectedDelivery from '../../../../CommonComponents/OrderStatus/ExpectedDelivery';
import LinkProvider from '../../../../CommonComponents/LinkProvider/LinkProvider';
import Carousel from '../../Carousel/Carousel';
import './TrackingTimeline.scss';

const MAX_SLIDES = 5;

const carouselConfig = {
  autoplay: false,
  infinite: false,
  slidesToScroll: 1,
  nextArrow: (
    <CustomeNavButton aria-label="Click here to see next slide">
      <i className="fa fa-angle-right fw700 fs18 pos-rel text-color-66"></i>
    </CustomeNavButton>
  ),
  prevArrow: (
    <CustomeNavButton aria-label="Click here to see previous slide">
      <i className="fa fa-angle-left fw700 fs18 pos-rel text-color-66"></i>
    </CustomeNavButton>
  ),
};

function CustomeNavButton({ currentSlide, slideCount, children, ...props }) {
  return <button {...props}>{children}</button>;
}

function openVideo(updateEmbedVideo, evt) {
  window.history.pushState({ embedVideo: true }, 'Embed YouTube Video', null);
  updateEmbedVideo({ embedVideo: evt.currentTarget.dataset.video });
}

function showTooltip(ev) {
  const el = ev.target;
  const title = el.textContent;
  const overflowed = el.scrollWidth > el.clientWidth;
  el.title = overflowed ? title : '';
}

function getTrackingData(
  histories,
  deliveryDate,
  embedVideoFunc,
  revisedEta,
  dataLocale,
  orderId,
  itemId
) {
  const { WATCH_VIDEO, TRACKING_ICON, VIDEO_PLAY_ICON, VIEW_DETAILS } = dataLocale || {};
  const trackingData = [];
  let initialSlide = 0;
  for (let i = 0, len = histories.length; i < len; i++) {
    const {
      isVisible,
      createdAt,
      current,
      iconUrl,
      statusLabel,
      status,
      descriptionLink,
      subTitle,
      showReturnDetails,
    } = histories[i];
    if (isVisible) {
      trackingData.push(
        <div
          key={trackingData.length}
          className="history text-center margin-t25 margin-b20"
          data-testid="history"
        >
          <div className="status-info display-flex justify-content-center align-items-center pos-rel">
            <div className="status-icon pos-rel">
              {current ? (
                <span className="img-icon">
                  <img alt={TRACKING_ICON} aria-label="Current Status Icon" src={iconUrl} />
                </span>
              ) : (
                <span
                  className="round-icon bora-50cent inline-block bg-color-white"
                  data-testid="roundIcon"
                ></span>
              )}
            </div>
            {trackingData.length > 0 && <span className="process-bar"></span>}
          </div>
          <div
            className={`margin-t5 fs12 fw700 font-medium ${
              current ? 'text-color-bright-yellow' : 'text-color-66'
            }`}
          >
            {statusLabel}
          </div>
          {createdAt && (
            <div className="line-height-20 fs12 text-color-grey" data-testid={`historyDate-${i}`}>
              {formatDate(createdAt, 'D MMM')}
            </div>
          )}
          {showReturnDetails && (
            <LinkProvider
              className="text-color_link_blue mr-t8 fsp12 block"
              to={`/customer/my/order/return/confirm-details/${orderId}/${itemId}`}
            >
              {VIEW_DETAILS}
            </LinkProvider>
          )}
          {['DELIVERED_AT_STORE', 'DELIVERED_TO_CUSTOMER'].includes(status) &&
            deliveryDate &&
            !createdAt && (
              <div className="line-height-20 fs12 text-color-grey">
                <ExpectedDelivery
                  dataLocale={dataLocale}
                  deliveryDate={deliveryDate}
                  revisedEta={revisedEta}
                />
              </div>
            )}
          {descriptionLink && (
            <div
              className="inline-block cursor-pointer fs12 text-color-primary line-height-20"
              data-video={descriptionLink}
              onClick={embedVideoFunc}
            >
              {WATCH_VIDEO}
              <img
                alt={VIDEO_PLAY_ICON}
                className="margin-l5 video-icon"
                src="https://static.lenskart.com/media/desktop/img/video-player-icon.svg"
              />
            </div>
          )}
          {subTitle && (
            <div
              className="line-height-20 fs12 text-color-grey subtitle text-ellipsis"
              onFocus={showTooltip}
              onMouseOver={showTooltip}
            >
              {subTitle}
            </div>
          )}
        </div>
      );
      if (current) {
        initialSlide = Math.max(0, trackingData.length - MAX_SLIDES);
      }
    }
  }
  if (trackingData.length > 0 && trackingData.length < 3) {
    trackingData.unshift(
      <div key={trackingData.length} className="dummy-slide" data-testid="history" />
    );
    trackingData.push(
      <div key={trackingData.length + 1} className="dummy-slide" data-testid="history" />
    );
  }
  const slidesToShow = Math.min(trackingData.length, MAX_SLIDES);
  const config = Object.assign(carouselConfig, {
    slidesToShow,
    initialSlide,
  });
  return {
    data: trackingData,
    config,
  };
}

function TrackingTimeline(props) {
  const {
    itemTracking: { histories = [] },
    deliveryDate,
    orderId,
    itemId,
    updateEmbedVideo,
    revisedEta,
    dataLocale,
  } = props;
  const embedVideoFunc = useCallback(openVideo.bind(null, updateEmbedVideo), []);
  const [trackingData] = useState(
    getTrackingData(
      histories,
      deliveryDate,
      embedVideoFunc,
      revisedEta,
      dataLocale,
      orderId,
      itemId
    )
  );
  const { data, config } = trackingData;
  return (
    <div className="tracking-timeline padding-l40 padding-r40" data-testid="trackingTimeline">
      <Carousel carouselConfig={config} carouselHtmlData={data} />
    </div>
  );
}

const mapDispatchToProps = dispatch => {
  return {
    updateEmbedVideo: data => dispatch(embedVideo(data)),
  };
};

export default connect(null, mapDispatchToProps)(TrackingTimeline);
